import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';


const COUNTRIES = [
  { code: 'GB', name: 'United Kingdom',  phone: '+44 7700 900000' },
  { code: 'US', name: 'United States',   phone: '+1 (555) 000-0000' },
  { code: 'IE', name: 'Ireland',         phone: '+353 87 000 0000' },
  { code: 'AU', name: 'Australia',       phone: '+61 4XX XXX XXX' },
  { code: 'CA', name: 'Canada',          phone: '+1 (XXX) XXX-XXXX' },
  { code: 'NZ', name: 'New Zealand',     phone: '+64 21 XXX XXXX' },
  { code: 'ZA', name: 'South Africa',    phone: '+27 71 000 0000' },
  { code: 'DE', name: 'Germany',         phone: '+49 170 0000000' },
  { code: 'FR', name: 'France',          phone: '+33 6 00 00 00 00' },
  { code: 'ES', name: 'Spain',           phone: '+34 600 000 000' },
  { code: 'IT', name: 'Italy',           phone: '+39 320 000 0000' },
  { code: 'NL', name: 'Netherlands',     phone: '+31 6 00000000' },
  { code: 'PT', name: 'Portugal',        phone: '+351 91 000 0000' },
  { code: 'SE', name: 'Sweden',          phone: '+46 70 000 0000' },
  { code: 'NO', name: 'Norway',          phone: '+47 40 00 00 00' },
  { code: 'DK', name: 'Denmark',         phone: '+45 20 00 00 00' },
  { code: 'CH', name: 'Switzerland',     phone: '+41 78 000 0000' },
  { code: 'BE', name: 'Belgium',         phone: '+32 470 00 00 00' },
  { code: 'AE', name: 'UAE',             phone: '+971 50 000 0000' },
  { code: 'SG', name: 'Singapore',       phone: '+65 8000 0000' },
];

export default function AccountSettings() {
  const { trainer, isAdmin, refreshTrainer } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState('details');

  // ── Stripe state ──────────────────────────────────────────
  const [stripeStatus, setStripeStatus] = useState(null);   // null | object
  const [stripeLoading, setStripeLoading] = useState(false);
  const [stripeWorking, setStripeWorking] = useState(false);
  const [stripeMsg, setStripeMsg] = useState(null);          // { type: 'success'|'error', text }

  const loadStripeStatus = useCallback(async () => {
    setStripeLoading(true);
    try {
      const data = await api.get('/stripe/account/status');
      setStripeStatus(data);
    } catch {
      setStripeStatus({ connected: false });
    } finally {
      setStripeLoading(false);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const stripe = params.get('stripe');
    if (stripe === 'connected') {
      setTab('payments');
      setStripeMsg({ type: 'success', text: 'Stripe account connected successfully.' });
      navigate('/settings/account', { replace: true });
    } else if (stripe === 'pending') {
      setTab('payments');
      setStripeMsg({ type: 'error', text: 'Stripe setup incomplete. Please finish connecting your account.' });
      navigate('/settings/account', { replace: true });
    } else if (stripe === 'error') {
      setTab('payments');
      setStripeMsg({ type: 'error', text: 'Could not connect Stripe account. Please try again.' });
      navigate('/settings/account', { replace: true });
    }
  }, [location.search, navigate]);

  useEffect(() => {
    if (tab === 'payments' && stripeStatus === null) loadStripeStatus();
  }, [tab, stripeStatus, loadStripeStatus]);

  async function handleConnectStripe() {
    setStripeWorking(true);
    setStripeMsg(null);
    try {
      const { url } = await api.get('/stripe/connect/url?source=web');
      window.location.href = url;
    } catch (err) {
      setStripeMsg({ type: 'error', text: err.message || 'Could not start Stripe connection.' });
      setStripeWorking(false);
    }
  }

  async function handleDisconnectStripe() {
    if (!window.confirm('Disconnect your Stripe account? You will no longer be able to take payments until you reconnect.')) return;
    setStripeWorking(true);
    setStripeMsg(null);
    try {
      await api.post('/stripe/disconnect');
      setStripeStatus({ connected: false });
      setStripeMsg({ type: 'success', text: 'Stripe account disconnected.' });
    } catch (err) {
      setStripeMsg({ type: 'error', text: err.message || 'Could not disconnect.' });
    } finally {
      setStripeWorking(false);
    }
  }
  const [form, setForm] = useState({
    name: trainer?.name || '',
    email: trainer?.email || '',
    business_name: trainer?.business_name || '',
    website: trainer?.website || '',
    unit_system: trainer?.unit_system || 'metric',
    country: trainer?.country || 'GB',
  });
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  async function handleSaveDetails(e) {
    e.preventDefault();
    setSaving(true); setError(''); setSuccess('');
    try {
      await api.put('/settings/account', form);
      await refreshTrainer();
      setSuccess('Account updated successfully');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleChangePassword(e) {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm_password) { setError('New passwords do not match'); return; }
    if (pwForm.new_password.length < 6) { setError('Password must be at least 6 characters'); return; }
    setSaving(true); setError(''); setSuccess('');
    try {
      await api.put('/settings/account/password', { current_password: pwForm.current_password, new_password: pwForm.new_password });
      setSuccess('Password changed successfully');
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-bg)' }}>
      <div className="page-header">
        <h1>
          <button className="icon-btn" style={{ marginRight: 4 }} onClick={() => navigate(-1)}>
            <span style={{ fontSize: 22, color: 'var(--color-text)' }}>‹</span>
          </button>
          Account Settings
        </h1>
      </div>

      <div style={{ display: 'flex', borderBottom: '1px solid var(--color-border)', background: 'var(--color-surface)' }}>
        {[['details', 'Details'], ['password', 'Password'], ['payments', 'Payments']].map(([key, label]) => (
          <button key={key} onClick={() => { setTab(key); setError(''); setSuccess(''); }}
            style={{ flex: 1, padding: '12px', background: 'transparent', border: 'none', borderBottom: tab === key ? '2px solid var(--color-primary)' : '2px solid transparent', color: tab === key ? 'var(--color-primary)' : 'var(--color-text-muted)', fontFamily: 'var(--font)', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
            {label}
          </button>
        ))}
      </div>

      <div className="page-content">
        {error && <div className="error-msg">{error}</div>}
        {success && <div className="success-msg">{success}</div>}

        {tab === 'details' && (
          <form onSubmit={handleSaveDetails}>
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 16 }}>Personal Info</div>
              <div className="form-group">
                <label className="form-label">Full Name</label>
                <input className="form-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Email</label>
                <input className="form-input" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required />
              </div>
            </div>

            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 16 }}>Business Info</div>
              <div className="form-group">
                <label className="form-label">Business Name</label>
                {isAdmin ? (
                  <input className="form-input" value={form.business_name} onChange={e => setForm(f => ({ ...f, business_name: e.target.value }))} placeholder="Your business name" />
                ) : (
                  <>
                    <div className="form-input" style={{ display: 'flex', alignItems: 'center', opacity: 0.6, cursor: 'not-allowed' }}>{trainer?.business_name || '—'}</div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4 }}>Contact your Admin to update business information.</div>
                  </>
                )}
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Website</label>
                {isAdmin ? (
                  <>
                    <input className="form-input" type="url" value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} placeholder="https://..." />
                    <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4 }}>Your website will appear on client invoices.</div>
                  </>
                ) : (
                  <>
                    <div className="form-input" style={{ display: 'flex', alignItems: 'center', opacity: 0.6, cursor: 'not-allowed' }}>{trainer?.website || '—'}</div>
                    <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4 }}>Contact your Admin to update business information.</div>
                  </>
                )}
              </div>
            </div>

            <div className="card" style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 16 }}>Measurement System</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                {[['imperial', 'US', 'lbs · ft/in'], ['metric', 'UK / Metric', 'kg · cm']].map(([val, label, sub]) => (
                  <button key={val} type="button" onClick={() => setForm(f => ({ ...f, unit_system: val }))}
                    style={{ padding: '12px 10px', border: '2px solid', borderColor: form.unit_system === val ? 'var(--color-primary)' : 'var(--color-border)', background: form.unit_system === val ? 'rgba(215,38,56,0.08)' : 'var(--color-bg)', borderRadius: 'var(--radius)', cursor: 'pointer', textAlign: 'center' }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: form.unit_system === val ? 'var(--color-primary)' : 'var(--color-text)' }}>{label}</div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 3 }}>{sub}</div>
                  </button>
                ))}
              </div>
            </div>


            <div className="card" style={{ marginBottom: 24 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 16 }}>Country</div>
              <select className="form-input" value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))}>
                {COUNTRIES.map(c => (
                  <option key={c.code} value={c.code}>{c.name}</option>
                ))}
              </select>
            </div>
            <button className="btn btn-primary" type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
          </form>
        )}

        {tab === 'password' && (
          <form onSubmit={handleChangePassword}>
            <div className="card">
              <div className="form-group">
                <label className="form-label">Current Password</label>
                <input className="form-input" type="password" value={pwForm.current_password} onChange={e => setPwForm(f => ({ ...f, current_password: e.target.value }))} required placeholder="••••••••" />
              </div>
              <div className="form-group">
                <label className="form-label">New Password</label>
                <input className="form-input" type="password" value={pwForm.new_password} onChange={e => setPwForm(f => ({ ...f, new_password: e.target.value }))} required placeholder="••••••••" />
              </div>
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label">Confirm New Password</label>
                <input className="form-input" type="password" value={pwForm.confirm_password} onChange={e => setPwForm(f => ({ ...f, confirm_password: e.target.value }))} required placeholder="••••••••" />
              </div>
            </div>
            <button className="btn btn-primary" type="submit" disabled={saving} style={{ marginTop: 16 }}>{saving ? 'Saving...' : 'Change Password'}</button>
          </form>
        )}

        {tab === 'payments' && (
          <div>
            {stripeMsg && (
              <div className={stripeMsg.type === 'success' ? 'success-msg' : 'error-msg'} style={{ marginBottom: 16 }}>
                {stripeMsg.text}
              </div>
            )}

            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 16 }}>Stripe Connect</div>

              {stripeLoading ? (
                <div style={{ color: 'var(--color-text-muted)', fontSize: 14 }}>Checking status…</div>
              ) : stripeStatus?.connected ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <span style={{ fontSize: 14, color: 'var(--color-text-muted)' }}>Account</span>
                    <span style={{ fontSize: 14, color: 'var(--color-text)', fontWeight: 500 }}>{stripeStatus.accountEmail}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                    <span style={{ fontSize: 14, color: 'var(--color-text-muted)' }}>Payments</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: stripeStatus.chargesEnabled ? '#2ecc71' : '#e74c3c' }}>
                      {stripeStatus.chargesEnabled ? 'Enabled' : 'Pending setup'}
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                    <span style={{ fontSize: 14, color: 'var(--color-text-muted)' }}>Payouts</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: stripeStatus.payoutsEnabled ? '#2ecc71' : '#e74c3c' }}>
                      {stripeStatus.payoutsEnabled ? 'Enabled' : 'Pending setup'}
                    </span>
                  </div>
                  {!stripeStatus.chargesEnabled && (
                    <div style={{ fontSize: 12, color: '#f39c12', marginBottom: 16, lineHeight: 1.5 }}>
                      Your Stripe account needs additional information before you can take payments. Log in to your Stripe dashboard to complete setup.
                    </div>
                  )}
                  <button
                    className="btn"
                    style={{ width: '100%', border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-text-muted)', opacity: stripeWorking ? 0.5 : 1 }}
                    onClick={handleDisconnectStripe}
                    disabled={stripeWorking}
                  >
                    {stripeWorking ? 'Disconnecting…' : 'Disconnect Stripe'}
                  </button>
                </>
              ) : (
                <>
                  <p style={{ fontSize: 14, color: 'var(--color-text-muted)', marginBottom: 16, lineHeight: 1.6 }}>
                    Connect your Stripe account to take payments directly from clients. You'll be redirected to Stripe to complete the setup.
                  </p>
                  <button
                    className="btn btn-primary"
                    style={{ width: '100%', opacity: stripeWorking ? 0.5 : 1 }}
                    onClick={handleConnectStripe}
                    disabled={stripeWorking}
                  >
                    {stripeWorking ? 'Redirecting…' : 'Connect Stripe'}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Legal ─────────────────────────────────────── */}
        <div className="card" style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>Legal</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <a href="/privacy" style={{ fontSize: 14, color: 'var(--color-text)', textDecoration: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              Privacy Policy <span style={{ color: 'var(--color-text-muted)' }}>›</span>
            </a>
            <a href="/terms" style={{ fontSize: 14, color: 'var(--color-text)', textDecoration: 'none', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--color-border)', paddingTop: 8 }}>
              Terms of Service <span style={{ color: 'var(--color-text-muted)' }}>›</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
